import { Spine } from 'pixi-spine';
import { Container } from 'pixi.js';
import AssetsManager from '../../gamma-engine/core/assets/AssetsManager';
import { UpdateLayoutDescription } from '../../gamma-engine/core/view/UpdateLayoutDescription';
import { ScreenOrientation } from '../../gamma-engine/core/view/ScreenOrientation';
import IntroScreen from './IntroScreen';

export default class Character extends Container {
    private isClickAnimating: boolean = false;
    private isBaseGameWinAnimating: boolean = false;
    private isFreeGameWinAnimating: boolean = false;

    public baseGame: Spine;
    public freeGame: Spine;
    public blueGemSpine: Spine;

    constructor(isFreeSpin: boolean) {
        super();
        this.baseGame = new Spine(AssetsManager.spine.get('character'));
        this.freeGame = new Spine(AssetsManager.spine.get('character'));
        this.blueGemSpine = new Spine(AssetsManager.spine.get('BlueGem'))
        this.blueGemSpine.name = 'blueGemSpine'
        this.blueGemSpine.state.setAnimation(0,'animation',true)
        this.blueGemSpine.position.set(-860,-161.5);

        this.setupMixes(this.baseGame, [
            [CharacterAnimations.BASE_GAME_IDLE, CharacterAnimations.CLICK_ANIMATION],
            [CharacterAnimations.CLICK_ANIMATION, CharacterAnimations.BASE_GAME_IDLE],
            [CharacterAnimations.BASE_GAME_IDLE, CharacterAnimations.BASE_GAME_WIN],
            [CharacterAnimations.BASE_GAME_WIN, CharacterAnimations.BASE_GAME_IDLE]
        ]);

        this.setupMixes(this.freeGame, [
            [CharacterAnimations.FREE_GAME_IDLE, CharacterAnimations.FREE_GAME_WIN],
            [CharacterAnimations.FREE_GAME_WIN, CharacterAnimations.FREE_GAME_IDLE],
        ]);

        this.baseGame.visible = !isFreeSpin;
        this.freeGame.visible = isFreeSpin;

        this.setupPointerEvents();
        this.addChild(this.baseGame, this.freeGame, this.blueGemSpine);
        this.on('added', this.onAdded, this);
    }

    private onAdded(): void {
        this.baseGame.state.setEmptyAnimations(0);
        this.freeGame.state.setEmptyAnimations(0);
        this.baseGame.state.setAnimation(0, CharacterAnimations.BASE_GAME_IDLE, true);
        this.freeGame.state.setAnimation(0, CharacterAnimations.FREE_GAME_IDLE, true);
    }

    private handleCharacterClick(): void {
        // Skip click if in free game mode or during animations
        if (this.freeGame.visible || this.isClickAnimating || this.isBaseGameWinAnimating) return;

        this.isClickAnimating = true; // Disable further clicks immediately
        const state = this.baseGame.state;
        const clickName = CharacterAnimations.CLICK_ANIMATION;
        state.clearListeners();

        // Start click animation
        const entry = state.setAnimation(0, clickName, false);
        entry.trackTime = 0;
        entry.mixDuration = 0.15;

        /* eslint-disable */
        const onComplete = (e: any) => {
            if (e?.animation?.name === clickName) {
                state.setAnimation(0, CharacterAnimations.BASE_GAME_IDLE, true);
                this.isClickAnimating = false; // Re-enable clicks after animation completes
                state.clearListeners();
            }
        };

        state.addListener({
            complete: onComplete,
        });
    }

    private setupPointerEvents(): void {
        this.eventMode = 'static';

        this.on('pointerover', () => {
            if (!this.freeGame.visible) {
                this.cursor = 'pointer';
            }
        });

        this.on('pointerout', () => {
            this.cursor = '';
        });

        this.on('pointerdown', () => this.handleCharacterClick());
    }

    private setupMixes(spine: Spine, mixes: CharacterAnimations[][]): void {
        spine.stateData.defaultMix = 0.15;
        mixes.forEach(([from, to]) => {
            spine.stateData.setMix(from, to, 0.2);
        });
    }

    public updateLayout(desc: UpdateLayoutDescription): void {
        const isIntroScreen = this.isInIntroScreen();
        this.blueGemSpine.visible = isIntroScreen || desc.orientation !== ScreenOrientation.VERTICAL;
    }

    private isInIntroScreen(): boolean {
        let parent = this.parent;
        while (parent) {
            if (parent instanceof IntroScreen) {
                return true;
            }
            parent = parent.parent;
        }
        return false;
    }
    
    public playWinAnimation(isFreeSpin: boolean) {
        const flag = isFreeSpin ? this.isFreeGameWinAnimating : this.isBaseGameWinAnimating;
        if (flag) return;

        isFreeSpin ? this.isFreeGameWinAnimating = true : this.isBaseGameWinAnimating = true;
        this.cursor = ''; // Hide cursor when win animation starts

        const spine = isFreeSpin ? this.freeGame : this.baseGame;
        const animName = isFreeSpin ? CharacterAnimations.FREE_GAME_WIN : CharacterAnimations.BASE_GAME_WIN;
        const idleName = isFreeSpin ? CharacterAnimations.FREE_GAME_IDLE : CharacterAnimations.BASE_GAME_IDLE;

        this.playAnimationWithReturn(spine, animName, idleName, isFreeSpin);
    }

    private playAnimationWithReturn(
        spine: Spine,
        animationName: string,
        idleName: string,
        isFreeSpin: boolean = false
    ): void {
        const state = spine.state;
        state.clearListeners();

        const entry = state.setAnimation(0, animationName, false);
        entry.mixDuration = 0.2;

        const returnToIdle = () => {
            state.setAnimation(0, idleName, true);
            isFreeSpin ? this.isFreeGameWinAnimating = false : this.isBaseGameWinAnimating = false;
            this.isClickAnimating = false; // Reset click flag so clicks work again
            state.clearListeners();
            this.cursor = 'pointer'; // Restore cursor after animation
        };

        state.addListener({
            complete: (evt: any) => {
                if (evt?.animation?.name === animationName) {
                    returnToIdle();
                }
            },
        });
    }
}

enum CharacterAnimations {
    BASE_GAME_IDLE = 'idle_basegame',
    BASE_GAME_WIN = 'win_basegame',
    FREE_GAME_IDLE = 'idle_freegame',
    FREE_GAME_WIN = 'win_freegame',
    CLICK_ANIMATION = 'click_hover',
}